/*
NOTE: this is a deliberate duplicate of shared/utils/src/hafas-reference.ts.
That's the canonical version (also used by backend/journey-client/src/oebb-client.ts
to work around cross-provider refresh-token quirks). It couldn't be
imported directly here because Create React App's default webpack config
only transpiles TypeScript inside src/, not raw .ts from node_modules
(even symlinked pnpm workspace packages) - so for now, keep this file in
sync manually when the canonical version changes.

example: ¶HKI¶T$A=1@O=Lier(B)@X=4559976@Y=51135857@L=8800083@a=128@$A=1@O=Antwerpen-Berchem@X=4432464@Y=51199528@L=8800008@a=128@$202604181028$202604181037$IC 4930$$1$$$$$$§T$A=1@O=Antwerpen-Berchem@X=4432464@Y=51199528@L=8800008@a=128@$A=1@O=Mechelen@X=4483469@Y=51017505@L=8800010@a=128@$202604181045$202604181103$IC 3332$$1$$$$$$
  start:
    ¶HKI¶
  leg 1:
    start:
      T
    from?
      $A=1@O=Lier(B)@X=4559976@Y=51135857@L=8800083@a=128@
    to?
      $A=1@O=Antwerpen-Berchem@X=4432464@Y=51199528@L=8800008@a=128@
    trip?
      $202604181028$202604181037$IC 4930$$1$$$$$$
  leg divider?
    §   
  leg 2:
    start:
      T
    from?
      $A=1@O=Antwerpen-Berchem@X=4432464@Y=51199528@L=8800008@a=128@
    to?
      $A=1@O=Mechelen@X=4483469@Y=51017505@L=8800010@a=128@
    trip?
      $202604181045$202604181103$IC 3332$$1$$$$$$
*/

export type HafasReference = {
  start: string,
  end: string,
  legs: HafasLeg[],
  __orig?: string,
}

export type HafasLeg = {
  prefix: string,
  from: HafasLocation,
  to: HafasLocation,
  trip: HafasTrip,
}

// Represented as key-value pairs (e.g., A=1, O=Lier) to support any order/properties
export type HafasLocation = {
  L: string,
  [key: string]: string
};

export type HafasTrip = {
  departureAt: string,
  arrivalAt: string,
  lineName: string,
  extras: string[],
}

export function decodeHafasReference(str: string): HafasReference {
  const prefixMatch = str.match(/^(¶[^¶]*¶)/)
  const start = prefixMatch ? prefixMatch[1] : ''
  const body = prefixMatch ? str.slice(start.length) : str

  const legStrings = body.split('§')
  const legs: HafasLeg[] = []

  for (const legStr of legStrings) {
    const trimmed = legStr.trim()
    if (!trimmed) continue

    // A leg is separated into parts by '$'.
    // Example: T$A=1@O=...$A=1@O=...$202604181028$202604181037$IC 4930$$1$$$$$$
    const parts = trimmed.split('$')

    // Expect at least [prefix (e.g. 'T'), fromLocation, toLocation, departureAt, arrivalAt, lineName]
    if (parts.length < 6) continue

    const from = parseLocation(parts[1])
    const to = parseLocation(parts[2])

    const trip: HafasTrip = {
      departureAt: parts[3] || '',
      arrivalAt: parts[4] || '',
      lineName: parts[5] || '',
      extras: parts.slice(6),
    }

    legs.push({ prefix: parts[0], from, to, trip })
  }

  return { start, end: '', legs, __orig: str }
}

function parseLocation(str: string): HafasLocation {
  const loc: Record<string, string> = Object.create(null)
  const props = str.split('@')
  for (const prop of props) {
    if (!prop) continue
    const eqIndex = prop.indexOf('=')
    const key = eqIndex === -1 ? prop : prop.slice(0, eqIndex)
    // Prevent prototype pollution from untrusted tokens
    if (key === '__proto__' || key === 'constructor' || key === 'prototype') continue
    loc[key] = eqIndex === -1 ? '' : prop.slice(eqIndex + 1)
  }
  return loc as HafasLocation
}

export function encodeHafasReference(ref: HafasReference): string {
  const legs = ref.legs.map(leg => {
    const fromStr = encodeLocation(leg.from)
    const toStr = encodeLocation(leg.to)
    const tripStr = encodeTrip(leg.trip)
    // The leg starts with its prefix (e.g. T), then $ delimiters
    return `${leg.prefix}$${fromStr}$${toStr}$${tripStr}`
  }).join('§')

  return `${ref.start}${legs}${ref.end}`
}

function encodeLocation(loc: HafasLocation): string {
  let str = ''
  for (const key of Object.keys(loc)) {
    str += `${key}=${loc[key]}@`
  }
  return str
}

function encodeTrip(trip: HafasTrip): string {
  return [trip.departureAt, trip.arrivalAt, trip.lineName, ...trip.extras].join('$')
}

export function stripRedundantAttributes(ref: HafasReference): HafasReference {
  const stripLocation = (loc: HafasLocation) => {
    const newLoc = { ...loc }
    delete newLoc.O
    delete newLoc.X
    delete newLoc.Y
    return newLoc
  }

  return {
    ...ref,
    legs: ref.legs.map(leg => ({
      ...leg,
      from: stripLocation(leg.from),
      to: stripLocation(leg.to),
    }))
  }
}

/*
Ported from iOS's HafasJourneyURLId.mergedShareMinimalHafasString (the
.departLater case) — splices a replacement journey's legs onto a base
journey's already-traveled legs, at the point they connect, so a single
real HAFAS reference spans the whole trip (start to finish) rather than
just the newly-searched replacement segment. This is the string that gets
handed to trip monitoring as a refreshToken, so it must decode/round-trip
exactly like a normal HAFAS token to the receiving service — no synthetic
prefix or wrapping.
*/
export function mergeDepartLaterHafasReference(
  baseRefreshToken: string,
  replacementRefreshToken: string,
): string | undefined {
  if (!baseRefreshToken || !replacementRefreshToken) return undefined

  const currentRef = decodeHafasReference(baseRefreshToken)
  const newRef = decodeHafasReference(replacementRefreshToken)
  if (currentRef.legs.length === 0 || newRef.legs.length === 0) return undefined

  const match = newRef.legs[0].from.L
  if (!match) return undefined

  const spliceIndex = currentRef.legs.findIndex(leg => leg.from.L === match)
  if (spliceIndex === -1) return undefined

  const merged: HafasReference = {
    start: newRef.start,
    end: '',
    legs: [...currentRef.legs.slice(0, spliceIndex), ...newRef.legs],
  }
  return encodeHafasReference(stripRedundantAttributes(merged))
}
